import { useState } from 'react';
import { X } from 'lucide-react';
import { CHORDS } from '@/data/chords';
import { t } from '@/i18n';
import { useDialog } from '@/hooks/useDialog';
import { ChordDiagram } from './ChordDiagram';
import './ChordPicker.css';

interface ChordPickerProps {
  /** Chords already chosen — shown as selected, tapping removes them. */
  selected: string[];
  onToggle: (chordId: string) => void;
  onClose: () => void;
}

/**
 * Bottom sheet for adding chords.
 *
 * A sheet rather than a separate route: the player is mid-setup with a guitar
 * on their lap, and losing the page they were configuring to pick one chord is
 * exactly the kind of friction this product is trying to remove.
 */
export function ChordPicker({ selected, onToggle, onClose }: ChordPickerProps) {
  const [query, setQuery] = useState('');
  const panelRef = useDialog(onClose);

  const normalized = query.trim().toLowerCase();
  const visible = normalized
    ? CHORDS.filter(
        (chord) =>
          chord.name.toLowerCase().includes(normalized) ||
          chord.nameRu.toLowerCase().includes(normalized),
      )
    : CHORDS;

  return (
    <div className="picker" role="dialog" aria-modal="true" aria-label={t('setup.pickChord')}>
      {/* A click-catcher, not a control: it is hidden from assistive tech so
          the sheet has exactly one "close" affordance, plus Escape. */}
      <div className="picker__scrim" onClick={onClose} aria-hidden="true" />
      <div className="picker__sheet" ref={panelRef}>
        <div className="picker__head">
          <h2 className="picker__title">{t('setup.pickChord')}</h2>
          <button
            type="button"
            className="picker__close"
            onClick={onClose}
            aria-label={t('setup.close')}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <input
          className="picker__search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('chords.search')}
          aria-label={t('chords.search')}
        />

        <ul className="picker__grid">
          {visible.map((chord) => {
            const isSelected = selected.includes(chord.id);
            return (
              <li key={chord.id}>
                <button
                  type="button"
                  className="picker__item"
                  aria-pressed={isSelected}
                  onClick={() => onToggle(chord.id)}
                >
                  <span className="picker__item-name">{chord.name}</span>
                  <ChordDiagram chord={chord} size={72} showFingers={false} frets={4} />
                </button>
              </li>
            );
          })}
        </ul>

        {visible.length === 0 && <p className="picker__empty">{t('chords.empty')}</p>}
      </div>
    </div>
  );
}
